import { reverse } from 'named-urls'
import React from 'react'

import { useDashboardPermissions } from '@components'
import { ButtonClean, ProfitString, Summary, useModalContext } from '@fourthwall/components'
import type { Price } from '@fourthwall/services/dist/models/common'
import { pluralize } from '@fourthwall/utils/dist/string'
import { getNumberOfProducts } from '@utils/products'
import { routing } from '@utils/routing'

import { SummaryRowLabel } from '../../../../components/SummaryRowLabel/SummaryRowLabel'
import type { Order } from '../../Order.types'
import { OrderCostsBreakdownModal } from '../../modules/OrderCostsBreakdownModal'
import { ErrorBadge } from '../ErrorBadge/ErrorBadge'
import * as S from './OrderProfitSummary.styled'

type OrderProfitSummaryProps = {
  order: Order
  shippingMethodTooltip?: string
  totalRefundedAmount?: Price
}

export const OrderProfitSummary = ({
  order,
  shippingMethodTooltip,
  totalRefundedAmount,
}: OrderProfitSummaryProps) => {
  const { open } = useModalContext()
  const { hasPermission } = useDashboardPermissions()
  const numberOfProducts =
    getNumberOfProducts(order.offers) + getNumberOfProducts(order.offersCancelled)

  const getShippingMethodLabel = () => {
    if (order.shipments.length > 1) {
      return 'Shipping (Multiple shipments)'
    }

    return order.shipping.method || ''
  }

  const getPromotionLabel = () => {
    if (!order || !order.promotion) return null

    return order.promotion.isAutoApplied ? order.promotion.title : order.promotion.code
  }

  const getDiscountLabel = () => {
    if (!order || !order.promotion) return

    const label = order.type === 'GIFT' ? 'Gift discount' : 'Discount'
    const promotionLabel = getPromotionLabel()

    return (
      <>
        {label}{' '}
        {promotionLabel && (
          <>
            (
            <ButtonClean
              label={promotionLabel}
              to={reverse(routing.promotions.promotionCodes.promotionCode.self, {
                promotionId: order.promotion.id,
              })}
            />
            )
          </>
        )}
      </>
    )
  }

  const getGiftCardLabel = (giftCard: NonNullable<Order['giftCards']>['giftCards'][number]) => {
    return (
      <>
        Gift card (
        <ButtonClean
          label={giftCard.code}
          to={reverse(routing.promotions.giftCards.giftCard.self, {
            giftCardId: giftCard.giftCardGroupId ?? giftCard.giftCardId,
          })}
        />
        )
      </>
    )
  }

  const getRefundLabel = () => {
    if (order.refund.anyRefundIssuedManually) {
      if (order.refund.anyRefundIssuedViaCancel) {
        // If includes both manual and cancel refunds
        return 'Refunds & adjustments'
      }

      // If includes manual refund only
      return 'Manual refunds'
    }

    // If includes cancel refund only
    return 'Refunded (canceled items)'
  }

  const renderRefundSummaryRow = () => {
    if (!totalRefundedAmount?.value || totalRefundedAmount.value <= 0) {
      return null
    }

    return (
      <Summary.Row
        label={getRefundLabel()}
        value={{
          value: totalRefundedAmount.value * -1,
          currency: totalRefundedAmount.currency,
        }}
      />
    )
  }

  const renderProfitSummaryRow = () => {
    if (!order) return null

    if (!hasPermission('contributions.profit')) return null

    const value = !order.profit ? (
      <ErrorBadge />
    ) : (
      <>
        <span style={{ marginRight: 8, fontSize: 12, fontWeight: 400 }}>
          <ButtonClean
            label="Show breakdown"
            appearance="secondary"
            onClick={() => {
              open(OrderCostsBreakdownModal, { orderId: order.id, order })
            }}
          />
        </span>
        <ProfitString value={order.profit.value} />
      </>
    )

    return <Summary.Row label="Your profit" value={value} bold />
  }

  return (
    <S.Container>
      <Summary>
        <Summary.Row
          label={`Subtotal (${pluralize(numberOfProducts, `${numberOfProducts} item`, `${numberOfProducts} items`)})`}
          value={order.merchandiseTotal}
        />
        {order.shipping.method && (
          <Summary.Row
            label={
              <SummaryRowLabel title={getShippingMethodLabel()} tooltip={shippingMethodTooltip} />
            }
            value={order.shipping.price}
          />
        )}
        {order.totalTax?.total && <Summary.Row label="Tax" value={order.totalTax.total} />}
        {order.paidPaymentFee && (
          <Summary.Row label="Payment processing fees" value={order.paidPaymentFee} />
        )}
        {!!order?.discount && (
          <Summary.Row
            label={getDiscountLabel()}
            value={{
              value:
                order.currentAmounts.discount.value > 0
                  ? order.currentAmounts.discount.value * -1
                  : order.currentAmounts.discount.value,
              currency: order.currentAmounts.discount.currency,
            }}
          />
        )}
        {order.giftCards?.giftCards?.map((giftCard) => (
          <Summary.Row
            label={getGiftCardLabel(giftCard)}
            value={{
              value:
                giftCard.amountUsed.value > 0
                  ? giftCard.amountUsed.value * -1
                  : giftCard.amountUsed.value,
              currency: giftCard.amountUsed.currency,
            }}
          />
        ))}
        {!!order.creatorBudget?.usedBudget && (
          <Summary.Row
            label="Samples budget"
            value={{
              value: order.creatorBudget.usedBudget.value * -1,
              currency: order.creatorBudget.usedBudget.currency,
            }}
          />
        )}
        {renderRefundSummaryRow()}
        <S.Separator />
        <Summary.Row label="Paid by supporter" value={order?.paidBySupporterFinal} bold />
        {renderProfitSummaryRow()}
      </Summary>
    </S.Container>
  )
}
